/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useCityContext } from "../../context/levels";
import { useCompanyContext } from "../../context/CompanyContext";
import { useAdminContext } from "../../context/admin/AdminContext";
import { useAdminsContext } from "../../context/admin/AdminManageContext";
import { useCountryContext } from "../../context/CountryContext";
import {
  MyMessage,
  InputField,
  PhoneInput,
  SelectInput,
  PasswordField,
  SearchSelectInput,
  AddPageShell,
  AddSection,
} from "../components/Component";
import { roles } from "./roles";
import { pushCountry } from "../../lib";
import { countriesList } from "../components/data";
import { HiOutlineUserAdd } from "react-icons/hi";
import { FiUser, FiLock, FiMapPin, FiShield } from "react-icons/fi";

const Add = () => {
  const { sysAdmin } = useAdminContext();
  const { create, setAdmins } = useAdminsContext();
  const currentRole = sysAdmin?.role;

  const { cities, fetchCities } = useCityContext();
  const { company, setCompany } = useCompanyContext();
  const { countries, fetchCountries } = useCountryContext();

  const [error, setError] = useState(false);
  const [message, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const [first_name, setFirstName] = useState(null);
  const [last_name, setLastName] = useState(null);
  const [email, setEmail] = useState(null);
  const [phone, setPhone] = useState(null);
  const [password, setPassword] = useState(null);
  const [country, setCountry] = useState("Uganda");
  const [city, setCity] = useState(company ? company?.city : null);
  const [role, setRole] = useState("Content Creator");
  const [address, setAddress] = useState(null);

  const adminRoutes = "/admin";

  useEffect(() => {
    if (!cities) fetchCities(1, 2000);
    if (!countries) fetchCountries();
  }, [cities, countries, country]);

  const cleanFields = () => {
    setLoading(false);
    setFirstName(null);
    setLastName(null);
    setEmail(null);
    setPhone(null);
    setPassword(null);
    setAddress(null);
  };

  const responses = (isError: any, msg: any) => {
    setError(isError);
    setLoading(false);
    setSuccess(!isError);
    setErrorMessage(msg);
  };

  const addOneTourPackageHandler = async (e: any) => {
    e.preventDefault();
    setLoading(true);

    if (!first_name || first_name === "")
      return responses(true, "first name is required");
    if (!last_name || last_name === "")
      return responses(true, "last name is required");
    if (!email || email === "") return responses(true, "email is required");
    if (!country || country === "")
      return responses(true, "country is required");
    if (!phone || phone === "")
      return responses(true, "phone number is required");
    if (!city || city === "") return responses(true, "city is required");
    if (!address || address === "")
      return responses(true, "address is required");
    if (!role || role === "") return responses(true, "role is required");
    if (!password || password === "")
      return responses(true, "password is required");

    await sendDBRequest();
  };

  const sendDBRequest = async () => {
    const data = {
      email,
      first_name,
      last_name,
      phonenumber: phone,
      password,
      country,
      city,
      address,
      company_id: sysAdmin?.company_id,
      role: String(role)?.toLowerCase()?.replaceAll(" ", "_"),
    };

    const { message, status } = await create(data);
    if (status === 200 || status === 201) {
      cleanFields();
      setAdmins(null);
      setCompany(null);
      responses(false, message);
    } else {
      responses(true, message);
    }
  };

  const citiesFilter = () => {
    const getId = ({ data, key, value, obj_id }: any) =>
      data?.find((x: any) => x?.[key] === value)?.[obj_id] || null;
    const id = getId({
      key: "name",
      value: country,
      data: countries,
      obj_id: "country_id",
    });
    return cities
      ?.filter((x: any) => x?.country_id === id)
      .map((y: any) => y?.name);
  };

  return (
    <AddPageShell
      backRoute={adminRoutes}
      eyebrow="Team • Admins"
      title="Create New Admin"
      subtitle="Add a teammate with the right role and location for permissions and localised content."
      icon={<HiOutlineUserAdd className="h-5 w-5" />}
      loading={loading}
      submitLabel="Add New Admin"
      onSubmit={addOneTourPackageHandler}
      onSubmitClick={() => {
        setError(false);
        setSuccess(false);
        setErrorMessage(null);
      }}
      message={
        <MyMessage
          error={error}
          message={message}
          success={success}
          onExpired={() => {
            setError(false);
            setSuccess(false);
          }}
          onClick={() => {
            setError(false);
            setSuccess(false);
            setErrorMessage("");
          }}
        />
      }
    >
      <AddSection
        title="Identity"
        hint="Basic details used across the dashboard."
        icon={<FiUser className="h-4 w-4" />}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <InputField
            props={{
              id: "first_name",
              title: "First Name",
              type: "text",
              required: true,
              value: first_name || "",
              onChange: (e: any) => {
                setFirstName(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
          <InputField
            props={{
              id: "last_name",
              title: "Last Name",
              type: "text",
              required: true,
              value: last_name || "",
              onChange: (e: any) => {
                setLastName(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
          <InputField
            props={{
              id: "email",
              title: "Email Address",
              type: "email",
              required: true,
              value: email || "",
              onChange: (e: any) => {
                setEmail(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
        </div>
      </AddSection>

      <AddSection
        title="Contact & Security"
        hint="Phone number and sign-in credentials."
        icon={<FiLock className="h-4 w-4" />}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <PhoneInput
            phone={phone}
            required={true}
            setPhone={setPhone}
            country={country || ""}
          />
          <PasswordField
            value={password || ""}
            required={true}
            onChange={(e: any) => {
              setPassword(e.target.value);
              setError(null);
              setSuccess(null);
            }}
          />
        </div>
      </AddSection>

      <AddSection
        title="Location"
        hint="Country and city for localised permissions and content."
        icon={<FiMapPin className="h-4 w-4" />}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <SelectInput
            title="Country"
            defaultValue={country || null}
            required={true}
            value={country || null}
            options={countries ? countries?.map((x: any) => x?.name) : []}
            onChange={(e: any) => {
              setCountry(e.target.value);
              setCity(null);
              const pickedCountry = countriesList?.find(
                (_c: any) =>
                  String(_c.name).toLowerCase() ===
                  String(e.target.value).toLowerCase(),
              );
              pushCountry({
                country: {
                  id: pickedCountry?.id,
                  name: pickedCountry?.name,
                  slug: pickedCountry?.name,
                  route: "/admin",
                },
              });
            }}
          />
          <SearchSelectInput
            title="City"
            setValue={setCity}
            value={city || null}
            required={true}
            options={
              cities
                ? country && !city
                  ? citiesFilter()
                  : cities?.map((x: any) => x?.name)
                : []
            }
            onChange={(value: any) => setCity(value)}
          />
          <InputField
            props={{
              id: "address",
              title: "Address",
              type: "address",
              required: true,
              value: address || "",
              onChange: (e: any) => {
                setAddress(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
        </div>
      </AddSection>

      <AddSection
        title="Access & Role"
        hint="Choose the role for the new admin — this controls what they can do."
        icon={<FiShield className="h-4 w-4" />}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <SelectInput
            title="Admin Role"
            defaultValue={role}
            required={true}
            options={roles(currentRole)}
            onChange={(e: any) => {
              setRole(e.target.value);
              setError(null);
              setSuccess(null);
            }}
          />
        </div>
      </AddSection>
    </AddPageShell>
  );
};

export default Add;
